import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Select, MenuItem, FormControl, Checkbox, ListItemText, Chip, Typography, Button, Popover, Badge, CircularProgress } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { fetchCustomTagsApi } from '../../api/workflowApi';


const TagFilterComponent = ({ selectedTags, onTagToggle, onClearAll, setCurrentPage }) => {
    const [allTags, setAllTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    let accessToken = useSelector((state) => state.auth.accessToken);

    useEffect(() => {
        fetchTags();
    }, []);

    const fetchTags = async (startUrl = null) => {
        if (tagsLoading) return;
        setTagsLoading(true);

        let allFetchedTags = [];
        let currentUrl = startUrl;

        try {
            while (true) {
                const data = await fetchCustomTagsApi(currentUrl);

                if (data?.results?.length) {
                    allFetchedTags = [
                        ...allFetchedTags,
                        ...data.results.filter(
                            newTag => !allFetchedTags.some(t => t.id === newTag.id)
                        )
                    ];
                }

                if (data?.next) {
                    currentUrl = data.next.split("/api/")[1];
                } else {
                    break;
                }
            }

            setAllTags(allFetchedTags);
        } catch (error) {
            console.error("Error fetching tags:", error);
        } finally {
            setTagsLoading(false);
        }
    };

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
        // Fetch tags when opening if not already loaded
        if (allTags.length === 0 && !tagsLoading) {
            fetchTags();
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const handleChange = (event) => {
        const value = event.target.value;
        const newTags = typeof value === 'string' ? value.split(',') : value;
        const addedTags = newTags.filter(tag => !selectedTags.includes(tag));
        const removedTags = selectedTags.filter(tag => !newTags.includes(tag));
        addedTags.forEach(tag => {
            onTagToggle(tag);
        });
        removedTags.forEach(tag => {
            onTagToggle(tag);
        });
        setCurrentPage(1)
    };

    const handleDelete = (tagToDelete) => (event) => {
        event.stopPropagation();
        onTagToggle(tagToDelete);
    };

    return (
        <>
            <Button
                onClick={handleFilterClick}
                startIcon={<FilterAltIcon />}
                sx={{
                    textTransform: 'none',
                    color: selectedTags.length > 0 ? '#4F63BE' : '#6B6B6B',
                    backgroundColor: '#fff',
                    border: '1px solid #DEDEDE',
                    borderRadius: '24px',
                    padding: '6px 16px',
                    marginRight: '10px',
                    fontSize: '14px',
                    fontWeight: 500,
                    minWidth: '140px',
                    maxHeight: '36px',
                    '&:hover': {
                        // backgroundColor: '#F9FAFB',
                        borderColor: '#4F63BE',
                    },
                    "& .MuiSelect-select": {
                        padding: "10px 14px !important",
                        minHeight: "auto !important",  // ADD this line
                    },
                }}
            >
                Filter
                {selectedTags.length > 0 && (
                    <Chip
                        label={selectedTags.length}
                        size="small"
                        sx={{
                            ml: 1,
                            height: '20px',
                            backgroundColor: '#4F63BE',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '12px',
                            '& .MuiChip-label': {
                                px: 1,
                            }
                        }}
                    />
                )}
            </Button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    mt: 1,
                }}
            >
                <Box sx={{ p: 2, minWidth: '320px', maxWidth: '400px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', fontSize: '15px' }}>
                            Filter by Tags
                        </Typography>
                        {selectedTags.length > 0 && (
                            <Button
                                onClick={() => {
                                    onClearAll();
                                }}
                                size="small"
                                sx={{
                                    textTransform: 'none',
                                    color: '#6B6B6B',
                                    fontSize: '13px',
                                    minWidth: 'auto',
                                    padding: '2px 8px',
                                    '&:hover': {
                                        backgroundColor: '#F9FAFB',
                                    }
                                }}
                            >
                                Clear all
                            </Button>
                        )}
                    </Box>

                    <FormControl fullWidth>
                        <Select
                            multiple
                            value={selectedTags}
                            onChange={handleChange}
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return (
                                        <Typography sx={{ color: "#9CA3AF", fontSize: "14px" }}>
                                            Choose tags
                                        </Typography>
                                    );
                                }

                                const MAX_VISIBLE = 2;
                                const visible = selected.slice(0, MAX_VISIBLE);
                                const extraCount = selected.length - MAX_VISIBLE;

                                return (
                                    <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, alignItems: 'center' }}>
                                        {visible.map((value) => {
                                            const tagObj = allTags.find((t) => t.id === value);
                                            return (
                                                <Chip
                                                    key={value}
                                                    label={tagObj?.name || value}
                                                    size="small"
                                                    onDelete={handleDelete(value)}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    sx={{
                                                        flexShrink: 0,
                                                        backgroundColor: "#E9EFF7",
                                                        color: "#4F63BE",
                                                        fontWeight: 500,
                                                        height: "24px",
                                                        fontSize: "13px",
                                                        "& .MuiChip-deleteIcon": {
                                                            fontSize: 14,
                                                            color: "#4F63BE",
                                                            '&:hover': {
                                                                color: '#3d4f9a',
                                                            },
                                                        },
                                                    }}
                                                />
                                            );
                                        })}
                                        {extraCount > 0 && (
                                            <Chip
                                                label={`+${extraCount} more`}
                                                size="small"
                                                sx={{
                                                    flexShrink: 0,
                                                    backgroundColor: "transparent",
                                                    color: "#6B7280",
                                                    height: "24px",
                                                    fontWeight: 500,
                                                    "& .MuiChip-label": {
                                                        padding: "0 8px",
                                                        fontSize: "12px",
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                );
                            }}
                            sx={{
                                minHeight: "40px",
                                borderRadius: "8px",
                                backgroundColor: "#fff",

                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#E5E7EB",
                                    borderWidth: "1px",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#4F63BE",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#4F63BE",
                                    borderWidth: "2px",
                                },

                                "& .MuiSelect-select": {
                                    padding: "10px 14px !important",
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        borderRadius: '8px',
                                        mt: 0.5,
                                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                                        maxHeight: '280px',
                                        '& .MuiMenuItem-root': {
                                            padding: '10px 12px',
                                            fontSize: '14px',
                                            '&:hover': {
                                                backgroundColor: '#F3F4F6',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#E9EFF7',
                                                '&:hover': {
                                                    backgroundColor: '#D4E3F5',
                                                },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            {tagsLoading ? (
                                <MenuItem disabled>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={20} sx={{ color: '#4F63BE' }} />
                                        <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                                            Loading tags...
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ) : allTags.length > 0 ? (
                                allTags.map((tag) => (
                                    <MenuItem key={tag.id} value={tag.id}>
                                        <Checkbox
                                            checked={selectedTags.includes(tag.id)}
                                            sx={{
                                                color: '#D1D5DB',
                                                padding: '4px',
                                                '&.Mui-checked': {
                                                    color: '#4F63BE',
                                                },
                                            }}
                                        />
                                        <ListItemText
                                            primary={tag.name}
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    fontSize: '14px',
                                                    color: '#374151',
                                                }
                                            }}
                                        />
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 2 }}>
                                        <Typography sx={{ color: '#9CA3AF', fontSize: '14px', textAlign: 'center' }}>
                                            No tags available
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Box>
            </Popover>
        </>
    );
};

export default TagFilterComponent;